import type { Curve, ParsedToolData, ToolType } from '../../interfaces/toolData'
import type { TimeShiftConfig } from '../../interfaces/models/TimeShift'
import { createDeviceKey } from '../../interfaces/models/TimeShift'

/**
 * Cached curve data with metadata
 */
interface CachedCurve {
  curve: Curve
  originalChecksum: string  // Simple checksum to detect data changes
  timestamp: number        // Cache creation time for expiration
}

/**
 * Core time shift engine that handles real-time data transformation
 * with millisecond precision and efficient caching
 */
export class TimeShiftEngine {
  private cache = new Map<string, CachedCurve>()
  private readonly maxCacheSize = 1000  // Prevent memory bloat
  private readonly cacheExpiryMs = 5 * 60 * 1000  // 5 minutes

  /**
   * Create a cache key for efficient lookups
   */
  private createCacheKey(filename: string, curveName: string, offsetMs: number): string {
    return `${filename}:${curveName}:${offsetMs}`
  }

  /**
   * Create a simple checksum for curve data to detect changes
   */
  private createChecksum(curve: Curve): string {
    const xLength = curve.xValues.length
    const yLength = curve.yValues.length
    const firstX = curve.xValues[0]
    const lastX = curve.xValues[xLength - 1]
    const firstY = curve.yValues[0]
    const lastY = curve.yValues[yLength - 1]
    
    return `${xLength}:${yLength}:${firstX}:${lastX}:${firstY}:${lastY}`
  }

  /**
   * Check if cached data is still valid
   */
  private isCacheValid(cached: CachedCurve, originalCurve: Curve): boolean {
    const now = Date.now()
    
    // Check if cache has expired
    if (now - cached.timestamp > this.cacheExpiryMs) {
      return false
    }
    
    // Check if original data has changed
    const currentChecksum = this.createChecksum(originalCurve)
    return cached.originalChecksum === currentChecksum
  }

  /**
   * Manage cache size to prevent memory bloat
   */
  private evictOldestCacheEntries(): void {
    if (this.cache.size <= this.maxCacheSize) return

    // Convert to array, sort by timestamp, and remove oldest entries
    const entries = Array.from(this.cache.entries())
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp)
    
    const toRemove = entries.slice(0, this.cache.size - this.maxCacheSize + 10) // Remove extra for efficiency
    toRemove.forEach(([key]) => this.cache.delete(key))
  }

  /**
   * Apply time shift to a curve's xValues with millisecond precision
   */
  applyTimeShift(
    originalCurve: Curve, 
    offsetMs: number, 
    filename: string = 'unknown'
  ): Curve {
    // No offset needed
    if (offsetMs === 0) {
      return originalCurve
    }

    // Check cache first
    const cacheKey = this.createCacheKey(filename, originalCurve.name, offsetMs)
    const cached = this.cache.get(cacheKey)
    
    if (cached && this.isCacheValid(cached, originalCurve)) {
      return cached.curve
    }

    // Apply time shift transformation
    const shiftedCurve = this.transformCurveData(originalCurve, offsetMs)
    
    // Cache the result
    this.evictOldestCacheEntries()
    this.cache.set(cacheKey, {
      curve: shiftedCurve,
      originalChecksum: this.createChecksum(originalCurve),
      timestamp: Date.now()
    })

    return shiftedCurve
  }

  /**
   * Core transformation logic for curve data
   * Note: Wave only works with Date objects for xValues (time series data)
   */
  private transformCurveData(originalCurve: Curve, offsetMs: number): Curve {
    if (originalCurve.xValues.length === 0) {
      return { ...originalCurve } // Return copy of empty curve
    }

    // Handle Date[] xValues (Wave only works with time-based data)
    const shiftedXValues = originalCurve.xValues.map(date => {
      return new Date(date.getTime() + offsetMs)
    })

    // Return new curve with shifted x values
    return {
      ...originalCurve,
      xValues: shiftedXValues,
      // Note: yValues remain unchanged - we're only shifting time
      yValues: [...originalCurve.yValues] // Shallow copy for safety
    }
  }

  /**
   * Get time shift offset for a specific device type from configuration
   */
  getTimeShiftForDevice(
    deviceType: ToolType, 
    deviceIdentifier: string | undefined,
    config: TimeShiftConfig | null
  ): number {
    if (!config) return 0

    const deviceKey = createDeviceKey(deviceType, deviceIdentifier)
    const shift = config.shifts.get(deviceKey)
    
    return shift?.timeOffsetMs ?? 0
  }

  /**
   * Get time shift offset for a parsed file based on its tool type
   */
  getTimeShiftForFile(
    parsedData: ParsedToolData,
    config: TimeShiftConfig | null
  ): number {
    if (!config || !parsedData.toolType) return 0

    // For now, we don't have device identifier extraction from parsed data
    // This could be enhanced later to extract serial numbers from metadata
    const deviceIdentifier = parsedData.metadata?.toolSerial || 
                             parsedData.metadata?.serialNumber

    return this.getTimeShiftForDevice(
      parsedData.toolType, 
      deviceIdentifier,
      config
    )
  }

  /**
   * Batch apply time shifts to multiple curves
   * Useful for processing all curves from a file at once
   */
  batchApplyTimeShifts(
    curves: Curve[],
    offsetMs: number,
    filename: string
  ): Curve[] {
    if (offsetMs === 0) {
      return curves // No transformation needed
    }

    return curves.map(curve => this.applyTimeShift(curve, offsetMs, filename))
  }

  /**
   * Invalidate cache entries for a specific file
   * Call this when file data changes
   */
  invalidateCacheForFile(filename: string): void {
    const keysToDelete: string[] = []
    
    this.cache.forEach((_, key) => {
      if (key.startsWith(`${filename}:`)) {
        keysToDelete.push(key)
      }
    })
    
    keysToDelete.forEach(key => this.cache.delete(key))
  }

  /**
   * Invalidate cache entries for a specific time offset
   * Call this when time shift configuration changes
   */
  invalidateCacheForOffset(offsetMs: number): void {
    const keysToDelete: string[] = []
    
    this.cache.forEach((_, key) => {
      if (key.endsWith(`:${offsetMs}`)) {
        keysToDelete.push(key)
      }
    })
    
    keysToDelete.forEach(key => this.cache.delete(key))
  }

  /**
   * Clear all cached data
   * Useful for memory cleanup or major configuration changes
   */
  clearCache(): void {
    this.cache.clear()
  }

  /**
   * Expose createDeviceKey function for external use
   */
  createDeviceKey(deviceType: ToolType, deviceIdentifier?: string): string {
    return createDeviceKey(deviceType, deviceIdentifier)
  }

  /**
   * Apply time shifts to all curves in a ParsedToolData object
   * Returns a new object with time-shifted curves, or the original if no shifts needed
   */
  applyCurvesTimeShift(parsedData: ParsedToolData, config: TimeShiftConfig): ParsedToolData {
    // Get the time shift for this device type
    const offsetMs = this.getTimeShiftForFile(parsedData, config)
    
    // No shift needed or no curves to process
    if (offsetMs === 0 || !parsedData.curves || parsedData.curves.length === 0) {
      return parsedData
    }

    // Generate filename from metadata or use default
    const filename = parsedData.metadata?.fileName || 
                    parsedData.metadata?.filename ||
                    `${parsedData.toolType}-${parsedData.fileType}`

    // Apply time shift to all curves
    const shiftedCurves = this.batchApplyTimeShifts(
      parsedData.curves,
      offsetMs,
      filename
    )

    // Return new object with shifted curves
    return {
      ...parsedData,
      curves: shiftedCurves
    }
  }

  /**
   * Get cache statistics for debugging and monitoring
   */
  getCacheStats(): {
    size: number
    maxSize: number
    oldestEntry: number | null
    newestEntry: number | null
  } {
    if (this.cache.size === 0) {
      return { size: 0, maxSize: this.maxCacheSize, oldestEntry: null, newestEntry: null }
    }

    const timestamps = Array.from(this.cache.values()).map(entry => entry.timestamp)
    
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      oldestEntry: Math.min(...timestamps),
      newestEntry: Math.max(...timestamps)
    }
  }

  /**
   * Check if a given time offset is within reasonable bounds
   * ±24 hours in milliseconds
   */
  isValidTimeOffset(offsetMs: number): boolean {
    const maxOffsetMs = 24 * 60 * 60 * 1000 // 24 hours
    return Math.abs(offsetMs) <= maxOffsetMs
  }

  /**
   * Utility to convert seconds to milliseconds for time offsets
   */
  static secondsToMs(seconds: number): number {
    return Math.round(seconds * 1000)
  }

  /**
   * Utility to convert milliseconds to seconds for display
   */
  static msToSeconds(ms: number): number {
    return ms / 1000
  }
}

// Export a singleton instance for global use
export const timeShiftEngine = new TimeShiftEngine()

// Export the class for testing and custom instances
export default TimeShiftEngine 